/**
 * ReviewClock — the AB 253 review clock's three faces (docs/PLAN_CHECK_PERFORMANCE.md):
 *   <ReviewClockChip>  a one-line chip for an agency review row / the Permits board
 *   <ReviewClockBar>   the 30-day track + 5-day notice window, for the dashboard
 *   <ReviewClockModal> the dates (deemed complete, estimate, notice sent); mounted once in
 *                      OutlineView, opened by CustomEvent('open-review-clock', { reviewId, reviewName })
 * The reading of the clock lives in services/reviewClock.js#clockState — one place.
 */
import { useEffect, useReducer, useState } from 'react'
import { clockState, cachedClock, saveReviewClock, suggestDeemedComplete, loadReviewClocks, CLOCK_DAYS, NOTICE_DAYS } from '../services/reviewClock.js'
import { todayIso } from '../services/planCheckPerformance.js'
import './planCheckPerformance.css'

export const CLOCK_TONE = {
  muted: 'var(--aj-text-muted)', calm: 'var(--aj-text-mid)', soon: '#b8540a', urgent: '#c0392b', done: 'var(--aj-accent)',
}

const fmt = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${+m[2]}/${+m[3]}/${m[1].slice(2)}` : '' }

/** Re-render on any clock change. */
export function useClockRefresh() {
  const [, bump] = useReducer(x => x + 1, 0)
  useEffect(() => { window.addEventListener('review-clocks-changed', bump); return () => window.removeEventListener('review-clocks-changed', bump) }, [])
}

export function openReviewClock(reviewId, reviewName) {
  window.dispatchEvent(new CustomEvent('open-review-clock', { detail: { reviewId, reviewName } }))
}

/** Row chip. `prompt` shows "Start the clock" when none is set (only where the clock applies). */
export function ReviewClockChip({ reviewId, reviewName, prompt = false }) {
  useClockRefresh()
  const clock = cachedClock(reviewId)
  if (clock === undefined) return null
  const st = clockState(clock)
  if (st.state === 'unset' && !prompt) return null
  const color = CLOCK_TONE[st.tone]
  return (
    <span role="button" tabIndex={0}
      className="aj-clock-chip"
      title={st.state === 'unset' ? 'Set the date the city deemed this application complete' : `30 business days from ${fmt(st.start)} · notice window ends ${fmt(st.windowEndsOn)}`}
      onClick={e => { e.stopPropagation(); openReviewClock(reviewId, reviewName) }}
      style={{ ...CHIP, color, borderColor: st.tone === 'urgent' ? color : 'var(--aj-border-mid)', fontWeight: st.tone === 'urgent' ? 600 : 400 }}
    >
      <ClockGlyph tone={st.tone} />{st.label}
    </span>
  )
}

function ClockGlyph({ tone }) {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" style={{ marginRight: 4, flexShrink: 0 }} aria-hidden="true">
      <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 3.2V6l2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      {tone === 'urgent' && <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" className="aj-clock-pulse" />}
    </svg>
  )
}

/** The track: 30 business days, then the 5-day notice window, with today's marker. */
export function ReviewClockBar({ reviewId, reviewName, compact = false }) {
  useClockRefresh()
  const clock = cachedClock(reviewId)
  const st = clockState(clock)
  if (clock === undefined) return null
  if (st.state === 'unset') {
    return (
      <button className="aj-clock-start" onClick={e => { e.stopPropagation(); openReviewClock(reviewId, reviewName) }} style={START}>
        Start the 30-business-day clock
      </button>
    )
  }
  const total = CLOCK_DAYS + NOTICE_DAYS
  const at = Math.min(total, st.day)
  const color = CLOCK_TONE[st.tone]
  return (
    <div onClick={e => { e.stopPropagation(); openReviewClock(reviewId, reviewName) }} style={{ cursor: 'pointer' }} title="Edit the review clock">
      <div style={{ display: 'flex', gap: 3, height: compact ? 6 : 8 }}>
        <div style={{ flex: CLOCK_DAYS, background: 'var(--aj-bg-muted)', borderRadius: '4px 0 0 4px', position: 'relative', overflow: 'hidden' }}>
          <div className="aj-clock-fill" style={{ position: 'absolute', inset: 0, width: `${Math.min(1, at / CLOCK_DAYS) * 100}%`, background: st.tone === 'done' ? 'var(--aj-accent)' : st.tone === 'calm' ? 'var(--aj-text)' : color, borderRadius: 'inherit' }} />
        </div>
        <div style={{ flex: NOTICE_DAYS, background: 'rgba(192,57,43,0.14)', borderRadius: '0 4px 4px 0', position: 'relative', overflow: 'hidden' }}>
          {at > CLOCK_DAYS && <div className="aj-clock-fill" style={{ position: 'absolute', inset: 0, width: `${Math.min(1, (at - CLOCK_DAYS) / NOTICE_DAYS) * 100}%`, background: '#c0392b' }} />}
        </div>
      </div>
      {!compact && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 11 }}>
          <span style={{ color, fontWeight: st.tone === 'urgent' ? 600 : 500 }}>{st.label}</span>
          <span style={{ color: 'var(--aj-text-muted)' }}>notify by {fmt(st.windowEndsOn)}</span>
        </div>
      )}
    </div>
  )
}

export function ReviewClockModal() {
  const [open, setOpen] = useState(false)
  const [reviewId, setReviewId] = useState(null)
  const [name, setName] = useState('')
  const [f, setF] = useState({})
  const [hint, setHint] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))

  useEffect(() => {
    async function onOpen(e) {
      const d = (e && e.detail) || {}
      if (!d.reviewId) return
      setReviewId(d.reviewId); setName(d.reviewName || 'Agency review'); setError(''); setHint(null); setBusy(false); setOpen(true)
      if (cachedClock(d.reviewId) === undefined) { try { await loadReviewClocks([d.reviewId]) } catch { /* shown on save */ } }
      const c = cachedClock(d.reviewId) || {}
      setF({ deemedCompleteOn: c.deemedCompleteOn || '', estimateBusinessDays: c.estimateBusinessDays || '', estimateReceivedOn: c.estimateReceivedOn || '', noticeSentOn: c.noticeSentOn || '', note: c.note || '' })
      if (!c.deemedCompleteOn) suggestDeemedComplete(d.reviewId).then(setHint).catch(() => {})
    }
    window.addEventListener('open-review-clock', onOpen)
    return () => window.removeEventListener('open-review-clock', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null
  const preview = clockState(f.deemedCompleteOn ? { ...f, estimateBusinessDays: Number(f.estimateBusinessDays) || null, stoppedOn: (cachedClock(reviewId) || {}).stoppedOn } : null)

  async function save() {
    setBusy(true); setError('')
    try { await saveReviewClock(reviewId, f); setOpen(false) } catch (err) { setError(err.message) }
    setBusy(false)
  }

  const S = MODAL
  const date = (k, label, sub) => (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      <input type="date" style={S.input} value={f[k] || ''} max={todayIso()} onChange={e => set(k, e.target.value)} />
      {sub}
    </label>
  )
  return (
    <div style={S.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
      <div data-modal-id="ReviewClockModal" style={S.box}>
        <div style={S.title}>Review clock</div>
        <div style={S.sub}>{name}</div>
        <div style={S.intro}>
          Once the city deems the application complete (here, when the plan check fee is paid) it has 30 business days
          to determine compliance, or must tell us it will take longer. When those 30 run out, or an estimate over 30 arrives, the owner may retain a private
          plan checker and must notify the city within 5 business days (Health and Safety Code 17960.3).
        </div>
        {date('deemedCompleteOn', 'Deemed complete on (the day the plan check fee was paid)', hint && hint.suggestedOn && !f.deemedCompleteOn && (
          <>
            <button style={S.hint} onClick={() => set('deemedCompleteOn', hint.suggestedOn)}>Use {fmt(hint.suggestedOn)} ({hint.basis})</button>
            {hint.feesUnlogged && <span style={S.warn}>No fee payment is logged on this review. Log "Paid Fees" with the date the client paid, and use that date here.</span>}
          </>
        ))}
        <div style={S.row}>
          <label style={{ ...S.field, flex: 1 }}>
            <span style={S.label}>City's estimate (business days)</span>
            <input style={S.input} value={f.estimateBusinessDays || ''} placeholder="if given" onChange={e => set('estimateBusinessDays', e.target.value.replace(/\D/g, ''))} />
          </label>
          <div style={{ flex: 1 }}>{date('estimateReceivedOn', 'Estimate received on')}</div>
        </div>
        {date('noticeSentOn', 'We notified the city of a private plan checker on')}
        <label style={S.field}>
          <span style={S.label}>Note</span>
          <input style={S.input} value={f.note || ''} placeholder="optional" onChange={e => set('note', e.target.value)} />
        </label>
        {f.deemedCompleteOn && (
          <div style={S.preview}>
            <span style={{ color: CLOCK_TONE[preview.tone], fontWeight: 600 }}>{preview.label}</span>
            <span style={{ color: 'var(--aj-text-muted)' }}> · day 30 is {fmt(preview.windowOpensOn)} · notify by {fmt(preview.windowEndsOn)}</span>
            <div style={{ color: 'var(--aj-text-muted)', marginTop: 4 }}>Weekends are skipped; city holidays are not, so treat the last day as approximate and act early.</div>
          </div>
        )}
        {error && <div style={S.error}>{error}</div>}
        <div style={S.actions}>
          <button style={S.btn} onClick={() => setOpen(false)}>Cancel</button>
          <button style={S.primary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

const ff = '"DM Sans",sans-serif'
const CHIP = { display: 'inline-flex', alignItems: 'center', fontFamily: ff, fontSize: 11, lineHeight: '16px', padding: '0 7px', borderRadius: 20, border: '0.5px solid', background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }
const START = { fontFamily: ff, fontSize: 11, color: 'var(--aj-accent-blue)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }
const MODAL = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  box: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: ff, color: 'var(--aj-text)', display: 'flex', flexDirection: 'column', gap: 10 },
  title: { fontSize: 13, fontWeight: 600 },
  sub: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: -8 },
  intro: { fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5 },
  row: { display: 'flex', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 3 },
  label: { fontSize: 11, color: 'var(--aj-text-muted)' },
  input: { fontFamily: ff, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', boxSizing: 'border-box', width: '100%' },
  warn: { fontSize: 11, color: '#b8540a', lineHeight: 1.4 },
  hint: { alignSelf: 'flex-start', fontFamily: ff, fontSize: 11, color: 'var(--aj-accent-blue)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' },
  preview: { fontSize: 12, padding: '9px 11px', borderRadius: 6, background: 'var(--aj-bg-subtle)', lineHeight: 1.5 },
  error: { color: '#c0392b', fontSize: 12 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 },
  btn: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  primary: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
